import { App } from './WebServiceContainer';
import { WebBroadcast } from './services/WebBroadcast';
import { AuthInstance, EventListItemWithPlayable } from '@signage-player/data-store';
import { LogLevel } from '@signage-player/contracts';

/**
 * Mounts a shim onto window.electronAPI that mirrors the shape of
 * apps/electron/src/preload/preload.ts but is backed by WebBroadcast and
 * direct in-process method calls. This lets us copy renderer code from
 * apps/electron without rewriting every electronAPI.* call.
 *
 * Call this BEFORE importing renderer code that touches window.electronAPI.
 */
export function installElectronAPIPolyfill(): void {
  const broadcast = App.broadcast() as WebBroadcast;

  const api = {
    app: {
      getVersion: async () => App.config().version,
      // mac/localIp aren't readable from a browser; publicIp is (pairing screen shows it).
      getStartupInfo: async () => ({
        localIp: null,
        publicIp: await App.config().publicIp,
        macAddress: null,
      }),
      // Bare language code — i18n matches SUPPORTED_LOCALES exactly ('cs', not 'cs-CZ').
      getLocale: async () => (navigator.language || 'en').split('-')[0],
      // Web has no binary/markers — behaviour markers are always off.
      getDebugFlags: async () => ({ renderer: false, fps: false, vsyncDisabled: false }),
      quitApplication: () => { App.lifecycle().quit(); },
      restartApplication: () => { App.lifecycle().relaunch(); },
    },

    auth: {
      getAuthInstance: async (): Promise<AuthInstance | null> => {
        return await App.auth().getAuthInstance();
      },
      onAuthInstanceUpdate: (cb: (authInstance: AuthInstance | null) => void) => {
        // Event name must match AuthService.broadcastAuthInstanceUpdate
        // (packages/api/src/AuthService.ts), which broadcasts 'auth-instance-update'.
        broadcast.on<[AuthInstance | null]>('auth-instance-update', cb);
      },
    },

    media: {
      onDownloadProgress: (cb: (progress: number, downloadedBytes: number, totalBytes: number) => void) => {
        broadcast.on<[number, number, number]>('media:downloadProgress', cb);
      },
    },

    player: {
      onInitialize: (cb: () => void) => {
        broadcast.on('player:initialize', () => { console.debug('[Polyfill] player:initialize'); cb(); });
      },
      onPreloadCommand: (cb: (item: EventListItemWithPlayable, odd: boolean, mediaPath: string) => void) => {
        broadcast.on<[EventListItemWithPlayable, boolean, string]>('player:preload', (item, odd, mediaPath) => {
          console.debug('[Polyfill] player:preload', { id: item?.id, odd, mediaPath });
          cb(item, odd, mediaPath);
        });
      },
      onPlayCommand: (cb: (item: EventListItemWithPlayable, odd: boolean, when: Date) => void) => {
        broadcast.on<[EventListItemWithPlayable, boolean, Date]>('player:play', (item, odd, when) => {
          console.debug('[Polyfill] player:play', { id: item?.id, odd, when });
          cb(item, odd, new Date(when));
        });
      },
      onStopCommand: (cb: (item: EventListItemWithPlayable, odd: boolean, when: Date, force: boolean) => void) => {
        broadcast.on<[EventListItemWithPlayable, boolean, Date, boolean]>('player:stop', (item, odd, when, force) => {
          console.debug('[Polyfill] player:stop', { id: item?.id, odd, when, force });
          cb(item, odd, new Date(when), force);
        });
      },
      onIdleCommand: (cb: (isSomethingPlanned: boolean, nextItemAt: Date | null, isExiting: boolean) => void) => {
        broadcast.on<[boolean, Date | null, boolean]>('player:idle', (s, n, e) => {
          console.debug('[Polyfill] player:idle', { isSomethingPlanned: s, nextItemAt: n, isExiting: e });
          cb(s, n ? new Date(n) : null, e ?? false);
        });
      },
      onSyncCommand: (cb: (item: EventListItemWithPlayable, odd: boolean, when: Date, seekTo: number) => void) => {
        broadcast.on<[EventListItemWithPlayable, boolean, Date, number]>('player:sync', (item, odd, when, seekTo) => {
          console.debug('[Polyfill] player:sync', { id: item?.id, odd, when, seekTo });
          cb(item, odd, new Date(when), seekTo);
        });
      },
    },

    utils: {
      removeAllListeners: (_channel: string) => {
        // EventTarget doesn't expose a remove-all hook; renderer-side cleanup
        // typically isn't required because the tab tears down everything together.
      },
    },

    logging: {
      onMainProcessLog: (cb: (logData: { level: string; timestamp: string; message: string; context?: unknown }) => void) => {
        // ConsoleLogger.broadcastLog emits 'main-process-log' on the same
        // WebBroadcast we own here; forward to the renderer-side callback so
        // the renderer's Logging module can print main-side logs to console.
        broadcast.on<[{ level: string; timestamp: string; message: string; context?: unknown }]>('main-process-log', cb);
      },
      removeMainProcessLogListener: () => { /* no-op */ },
      // Mirrors electron loggingHandlers: invalid level falls back to INFO,
      // renderer-origin logs are prefixed so they're distinguishable.
      sendLog: (logData: any) => {
        const { level, message, context } = logData ?? {};
        if (typeof message !== 'string') return;
        const safeLevel = Object.values(LogLevel).includes(level) ? (level as LogLevel) : LogLevel.INFO;
        App.logger().log(safeLevel, `[Renderer] ${message}`, context);
      },
    },

    healthcheck: {
      rendererAlive: () => {
        // RendererHealthTracker is Electron-specific. In a single-context web
        // app, the renderer IS the app; if it's not alive nothing else runs.
      },
    },
  };

  (window as any).electronAPI = api;
}
